import React from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Page from "components/Page/Page";
import Card from "components/Card/Card";
import Button from "components/Button/Regular";
import { useActiveOrg } from "context/ActiveOrgContext";
import vgToast from "utils/toast";
import { readSelfProfile } from "services/profile.service";
import { readOrganisation } from "services/clinics.service";
import { OrgResource, readOrgResources } from "services/config-assignments.service";
import {
  browserTimeZone,
  closeFacility,
  createFacility,
  deleteFacility,
} from "services/facilities.service";

const inputClass = "rounded-md border border-slate-300 px-3 py-2 text-sm";

/**
 * Cremators, chapels and rooms: what a crematorium's times are booked against. Each is
 * a Core resource with its own slot length. Assign one to a booking configuration under
 * Services to make it bookable; close it here for maintenance or a holiday. A facility's
 * time zone is the one at the provider's pin.
 */
const Facilities = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [facilities, setFacilities] = React.useState<OrgResource[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [name, setName] = React.useState("");
  const [slotMinutes, setSlotMinutes] = React.useState(60);
  // The provider's pin, or null until it has set its location.
  const [pin, setPin] = React.useState<{ latitude: number; longitude: number } | null>(null);
  const [adding, setAdding] = React.useState(false);

  const [closureFacility, setClosureFacility] = React.useState("");
  const [closureTitle, setClosureTitle] = React.useState("Maintenance");
  const [closureStart, setClosureStart] = React.useState("");
  const [closureEnd, setClosureEnd] = React.useState("");
  const [closing, setClosing] = React.useState(false);

  const load = React.useCallback(async () => {
    if (orgId <= 0) {
      setFacilities([]);
      return;
    }
    setLoading(true);
    setError(null);
    const token = await getAccessTokenSilently();
    const [{ data, error: readError }, { data: org }] = await Promise.all([
      readOrgResources(token, orgId),
      readOrganisation(token, orgId),
    ]);
    setLoading(false);
    // An organisation with no address was never placed: its latitude and longitude are
    // only Core's defaults.
    setPin(
      org?.address && org.latitude != null && org.longitude != null
        ? { latitude: org.latitude, longitude: org.longitude }
        : null
    );
    if (readError) {
      setError(readError.message);
      return;
    }
    setFacilities(data ?? []);
  }, [getAccessTokenSilently, orgId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const currentUserId = async (token: string): Promise<number | null> => {
    const { data, error: profileError } = await readSelfProfile(token);
    if (profileError || !data?.userId) {
      vgToast.error(profileError?.message ?? "Could not work out who you are.");
      return null;
    }
    return data.userId;
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    try {
      const token = await getAccessTokenSilently();
      const creatorId = await currentUserId(token);
      if (creatorId == null) return;
      const { error: addError } = await createFacility(token, {
        orgId,
        creatorId,
        name,
        slotMinutes,
        latitude: pin?.latitude ?? null,
        longitude: pin?.longitude ?? null,
      });
      if (addError) {
        vgToast.error(addError.message);
        return;
      }
      vgToast.success("Facility added.");
      setName("");
      await load();
    } finally {
      setAdding(false);
    }
  };

  const remove = async (facility: OrgResource) => {
    if (!window.confirm(`Remove ${facility.resourceName}? Its future bookings will need moving first.`)) return;
    const token = await getAccessTokenSilently();
    const { error: deleteError } = await deleteFacility(token, orgId, facility.resourceId);
    if (deleteError) {
      vgToast.error(deleteError.message);
      return;
    }
    vgToast.success("Facility removed.");
    await load();
  };

  const close = async (e: React.FormEvent) => {
    e.preventDefault();
    setClosing(true);
    try {
      const token = await getAccessTokenSilently();
      const creatorId = await currentUserId(token);
      if (creatorId == null) return;
      const { error: closeError } = await closeFacility(token, {
        orgId,
        creatorId,
        resourceIds: closureFacility ? [Number(closureFacility)] : [],
        title: closureTitle,
        start: closureStart,
        end: closureEnd,
        // The From and Until are read in this browser's time zone.
        timeZoneIANA: browserTimeZone(),
      });
      if (closeError) {
        vgToast.error(closeError.message);
        return;
      }
      vgToast.success("Closure saved. Those times are no longer bookable.");
      setClosureStart("");
      setClosureEnd("");
    } finally {
      setClosing(false);
    }
  };

  if (orgId <= 0) {
    return (
      <Page title="Facilities">
        <p className="text-sm text-slate-500">Choose an active provider from the header.</p>
      </Page>
    );
  }

  return (
    <Page title="Facilities" subTitle="Cremators, chapels and rooms that times are booked against.">
      <div className="flex flex-col gap-5">
        <Card title="Your facilities">
          <div className="p-6">
            {loading && <p className="text-sm text-slate-500">Loading facilities…</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
            {!loading && !error && facilities.length === 0 && (
              <p className="text-sm text-slate-500">
                No facilities yet. Add your cremators and chapels below, then assign them to a
                booking configuration under Services.
              </p>
            )}
            {facilities.length > 0 && (
              <ul className="divide-y divide-slate-100">
                {facilities.map((f) => (
                  <li key={f.resourceId} className="flex items-center justify-between py-3">
                    <span>
                      <span className="block text-sm font-medium text-slate-800">{f.resourceName}</span>
                      <span className="block text-xs text-slate-500">
                        {f.slotMinutes > 0 ? `${f.slotMinutes}-minute slots` : "No slot length set"}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:underline"
                      onClick={() => remove(f)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card title="Add a facility">
          <form onSubmit={add} className="p-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Name
              <input
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cremator 1 or Main chapel"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Slot length (minutes)
              <input
                className={inputClass}
                type="number"
                min={15}
                max={480}
                step={15}
                value={slotMinutes}
                onChange={(e) => setSlotMinutes(Number(e.target.value))}
              />
            </label>
            {!loading && !pin && (
              <p className="text-sm text-slate-500 sm:col-span-2">
                Set your location on your{" "}
                <Link className="underline" to={`/app/clinics/${orgId}`}>
                  provider page
                </Link>{" "}
                first: a facility's times are in the time zone where you are.
              </p>
            )}
            <div className="sm:col-span-2">
              <Button color="Primary" type="submit" disabled={adding}>
                <span>{adding ? "Adding…" : "Add facility"}</span>
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Close a facility" subTitle="For maintenance or a holiday. Those times stop being bookable.">
          <form onSubmit={close} className="p-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Facility
              <select
                className={inputClass}
                value={closureFacility}
                onChange={(e) => setClosureFacility(e.target.value)}
              >
                <option value="">Choose…</option>
                {facilities.map((f) => (
                  <option key={f.resourceId} value={f.resourceId}>
                    {f.resourceName}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Reason
              <input className={inputClass} value={closureTitle} onChange={(e) => setClosureTitle(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              From
              <input
                className={inputClass}
                type="datetime-local"
                value={closureStart}
                onChange={(e) => setClosureStart(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Until
              <input
                className={inputClass}
                type="datetime-local"
                value={closureEnd}
                onChange={(e) => setClosureEnd(e.target.value)}
              />
            </label>
            <div className="sm:col-span-2">
              <Button color="Primary" type="submit" disabled={closing}>
                <span>{closing ? "Saving…" : "Close facility"}</span>
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Page>
  );
};

export default Facilities;
